<template>
  <div class="ads" :class="{ 'ads--compact': compact }">
    <p v-if="!devices.labelsVisible" class="ads-note">
      O navegador só mostra o nome dos dispositivos depois de liberar o microfone.
      <button type="button" class="ads-link" @click="allowDevices">Liberar e listar</button>
    </p>
    <p v-if="devices.problem" class="ads-problem">{{ devices.problem }}</p>
    <p v-if="missingDevices.length" class="ads-problem">{{ missingText }} — usando o padrão do sistema até reconectar.</p>

    <div class="ads-grid">
      <div class="ads-field">
        <span class="ads-label">Microfone</span>
        <div class="ads-row">
          <select class="ads-select" :value="devices.choice.microphoneId" aria-label="Microfone" @change="pick('microphoneId', $event)">
            <option value="">Padrão do sistema</option>
            <option v-for="d in devices.inputs" :key="d.id" :value="d.id">{{ d.label }}</option>
          </select>
          <button type="button" class="ads-btn" @click="toggleMicTest">{{ micTesting ? 'Parar' : 'Testar' }}</button>
        </div>
        <div class="ads-meter" aria-hidden="true"><div :style="{ width: `${Math.round(micLevel * 100)}%` }"></div></div>
        <small v-if="micTesting" class="ads-help">Fale alguma coisa: a barra tem que mexer.</small>
        <small v-else-if="callActive" class="ads-help">Trocar agora vale a partir da próxima ligação.</small>
      </div>

      <div class="ads-field">
        <span class="ads-label">Saída da conversa</span>
        <div class="ads-row">
          <select class="ads-select" :value="devices.choice.speakerId" :disabled="!supportsOutputSelection" aria-label="Saída da conversa" @change="pick('speakerId', $event)">
            <option value="">Padrão do sistema</option>
            <option v-for="d in devices.outputs" :key="d.id" :value="d.id">{{ d.label }}</option>
          </select>
          <button type="button" class="ads-btn" @click="testOutput('speakerId')">Testar</button>
        </div>
        <small class="ads-help">Onde sai a voz do contato.</small>
      </div>

      <div class="ads-field">
        <span class="ads-label">Saída do toque</span>
        <div class="ads-row">
          <select class="ads-select" :value="devices.choice.ringerId" :disabled="!supportsOutputSelection" aria-label="Saída do toque" @change="pick('ringerId', $event)">
            <option value="">Padrão do sistema</option>
            <option v-for="d in devices.outputs" :key="d.id" :value="d.id">{{ d.label }}</option>
          </select>
          <button type="button" class="ads-btn" @click="testOutput('ringerId')">Testar</button>
        </div>
        <small class="ads-help">Ex.: o toque nas caixas de som e a conversa no headset.</small>
      </div>
    </div>

    <p v-if="!supportsOutputSelection" class="ads-note">
      Este navegador não deixa escolher a saída de áudio — o som sai no padrão do sistema. No Chrome ou no Edge dá.
    </p>
  </div>
</template>

<script setup lang="ts">
// Escolher e testar os dispositivos de áudio do telefone no navegador (useAudioDevices.ts). Usado no painel do
// telefone (PhoneWidget.vue, engrenagem) e em Admin › Linkus — lá funciona mesmo com o Linkus desligado, pra deixar
// o computador pré-configurado. A escolha fica neste navegador.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import Swal from 'sweetalert2'
import {
  effectiveDevices, missingDevices, playTestSound, refreshDevices, setDeviceChoice, startMicTest, supportsOutputSelection,
  useAudioDevices,
} from '@/composables/useAudioDevices'

defineProps<{
  /** Layout empilhado (painel do telefone); sem isto, colunas (Admin). */
  compact?: boolean
  /** Tem ligação em andamento — avisa que o microfone novo vale da próxima. */
  callActive?: boolean
}>()

const { devices } = useAudioDevices()
const micTesting = ref(false)
const micLevel = ref(0)
let stopMic: (() => void) | null = null

const missingText = computed(() => {
  const list = missingDevices.value
  return list.length === 1 ? `O ${list[0]} escolhido não está conectado` : `Não estão conectados: ${list.join(', ')}`
})

async function allowDevices() { await refreshDevices(true) }

function pick(key: 'microphoneId' | 'speakerId' | 'ringerId', e: Event) {
  setDeviceChoice({ [key]: (e.target as HTMLSelectElement).value })
  if (key === 'microphoneId' && micTesting.value) { stopMicTest(); void toggleMicTest() }
}

async function toggleMicTest() {
  if (micTesting.value) { stopMicTest(); return }
  try {
    stopMic = await startMicTest(effectiveDevices.value.microphoneId, level => { micLevel.value = level })
    micTesting.value = true
    if (!devices.labelsVisible) void refreshDevices()
  } catch {
    Swal.fire({ icon: 'warning', title: 'Microfone não liberado', text: 'Permita o microfone pra este site e tente de novo.' })
  }
}
function stopMicTest() {
  stopMic?.()
  stopMic = null
  micTesting.value = false
  micLevel.value = 0
}
function testOutput(key: 'speakerId' | 'ringerId') {
  void playTestSound(effectiveDevices.value[key], key === 'ringerId' ? 'ring' : 'tone')
}

onMounted(() => { void refreshDevices() })
onBeforeUnmount(stopMicTest)
</script>

<style scoped>
/* Só variáveis de tema: vale no claro e no escuro, e no painel do telefone e no Admin. */
.ads { display: flex; flex-direction: column; gap: 12px; }
.ads-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 14px; }
@media (max-width: 900px) { .ads-grid { grid-template-columns: 1fr; } }
.ads--compact .ads-grid { grid-template-columns: 1fr; gap: 12px; }
.ads-field { display: flex; flex-direction: column; gap: 5px; min-width: 0; }
.ads-label { font-size: 12px; font-weight: 600; color: var(--ink-60); }
.ads--compact .ads-label { font-size: 10.5px; font-weight: 700; color: var(--ink-40); text-transform: uppercase; letter-spacing: .06em; }
.ads-row { display: flex; gap: 6px; min-width: 0; }
.ads-select {
  flex: 1; min-width: 0; padding: 8px 10px; min-height: 36px; border: 1px solid var(--ink-10); border-radius: var(--r-sm);
  font: inherit; font-size: 13px; color: var(--ink-80); background: var(--white); cursor: pointer;
}
.ads--compact .ads-select { padding: 7px 8px; min-height: 34px; font-size: 12.5px; }
.ads-select:focus { border-color: var(--red); outline: none; }
.ads-select:disabled { opacity: .55; cursor: not-allowed; }
.ads-btn {
  flex-shrink: 0; padding: 0 12px; min-height: 36px; border-radius: var(--r-sm); border: 1px solid var(--ink-10);
  background: var(--ink-05); color: var(--ink-60); font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer;
}
.ads--compact .ads-btn { min-height: 34px; }
.ads-btn:hover { background: var(--ink-10); }
.ads-meter { height: 6px; border-radius: 4px; background: var(--ink-05); overflow: hidden; }
.ads-meter div { height: 100%; width: 0; border-radius: 4px; background: var(--green); transition: width .05s linear; }
[data-theme="dark"] .ads-meter div { background: #4ade80; }
.ads-help { font-size: 11.5px; color: var(--ink-40); line-height: 1.4; }
.ads-note { margin: 0; font-size: 12px; color: var(--ink-40); line-height: 1.5; }
.ads-link { background: none; border: none; padding: 0; font: inherit; font-size: inherit; font-weight: 600; color: var(--red); cursor: pointer; }
.ads-link:hover { text-decoration: underline; }
.ads-problem {
  margin: 0; font-size: 12px; line-height: 1.45; padding: 8px 10px; border-radius: var(--r-sm);
  background: rgba(255,102,0,.08); border: 1px solid rgba(255,102,0,.3); color: var(--orange);
}
</style>
